import { useId } from 'react';
import type { ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label: ReactNode;
  /** Helper text shown below the control. */
  hint?: ReactNode;
  /** Error message. When set, the control is marked invalid. */
  error?: ReactNode;
  className?: string;
  /** A single form control (Input, Textarea, …). Gets id/aria wired automatically. */
  children: ReactNode;
}

/**
 * Label + control + hint/error, with `id`, `aria-describedby` and
 * `aria-invalid` wired up. Wrap any of the form controls in `components/ui`.
 */
export function Field({ label, hint, error, className, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  const control =
    isValidElement(children) &&
    cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      id,
      'aria-describedby': describedBy,
      'aria-invalid': error ? true : undefined,
    });

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      {control || children}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-faint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-critical">
          {error}
        </p>
      )}
    </div>
  );
}
